import { useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';

import { isMobileAppUserAgentSelector } from '@/domains/core/client/client.selectors';
import { getMarket, getPlatform } from '@/domains/core/settings/utils';
import { getOrCreateVisitorId } from '@/domains/core/tracking/utils/visitorId';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';

import { getConversationIds } from '../utils/getConversationIds';

type ConversationData = {
  pageType: string;
  pageName: string;
};

const conversationKeys = [
  'platform',
  'pageType',
  'pageName',
  'productId',
  'modelId',
  'articleId',
  'market',
  'channel',
  'device',
] as const;
type ConversationKey = (typeof conversationKeys)[number];

export function useZendeskConversation(
  isScriptLoaded: boolean,
  { pageType, pageName }: ConversationData,
) {
  const ids = getConversationIds();
  const { productId, modelId, articleId } = useDetail();
  const platform = getPlatform();
  const isMobile = useSelector(isMobileAppUserAgentSelector);
  const getValueById = useCallback(
    (id: ConversationKey) => {
      if (id in ids) {
        switch (id) {
          case 'platform':
            return { id: ids[id], value: `${platform}_metadata` };
          case 'pageType':
            return { id: ids[id], value: pageType };
          case 'pageName':
            return { id: ids[id], value: pageName };
          case 'productId':
            return { id: ids[id], value: productId };
          case 'modelId':
            return { id: ids[id], value: modelId };
          case 'articleId':
            return { id: ids[id], value: articleId };
          case 'market':
            return {
              id: ids[id],
              value: `${getMarket().toLowerCase()}_market_metadata`,
            };
          case 'channel':
            return { id: ids[id], value: 'web' };
          case 'device':
            return { id: ids[id], value: isMobile ? 'phone' : 'desktop' };
          default:
            //cover all the possible values of ConversationKey at build time
            id satisfies never;
        }
      }
      return undefined;
    },
    [
      ids,
      pageType,
      pageName,
      productId,
      platform,
      modelId,
      articleId,
      isMobile,
    ],
  );

  useEffect(() => {
    if (isScriptLoaded) {
      window.zE?.(
        'messenger:set',
        'conversationFields',
        conversationKeys
          .map(getValueById)
          .filter((cv) => cv?.value !== undefined),
      );
    }

    //TODO: check if we need to reset the fields in web chat when unmounting component
  }, [isScriptLoaded, pageType, ids, getValueById]);

  useEffect(() => {
    let ignore = false;
    if (isScriptLoaded && ids['visitorId']) {
      getOrCreateVisitorId().then((value) => {
        if (!ignore) {
          window.zE?.('messenger:set', 'conversationFields', [
            { id: ids['visitorId'], value },
          ]);
        }
      });
    }

    return () => {
      ignore = true;
    };
  }, [isScriptLoaded, ids]);
}
